import styles from "./ParameterEnum.module.css";
import * as Menu from "src/components/ui-components/MenuPanel/MenuPanel";
import cn from "classnames";
import { MenuItemFreeformOption } from "./MenuItemFreeformOption";
import { Button } from "src/components/ui-components/Button/Button";
import { IconPlusSquare } from "src/components/ui-components/Icons/IconPlusSquare";

import { unique } from "src/utils/array";
import produce from "immer";

import type { Dispatch, SetStateAction } from "react";
import type { Parameter } from "src/utils/parameters/types";

type EnumOption = Exclude<Parameter["enumOptions"], undefined>[number];

interface Props {
  parameter: Parameter;
  parameterValues: Exclude<Parameter["values"], undefined>;
  onChange: (parameter: Parameter) => void;
  enumOptions: EnumOption[];

  freeformOptions: EnumOption[];
  setFreeformOptions: Dispatch<SetStateAction<EnumOption[]>>;
}

export function MenuSectionFreeformOptions(props: Props) {
  const {
    parameter,
    parameterValues: values,
    onChange,
    enumOptions,
    freeformOptions,
    setFreeformOptions,
  } = props;

  const { isMultiselect = false } = parameter;

  return (
    <Menu.Section
      renderTitle={({ className }) => (
        <div className={cn(className, styles.menuSectionTitleWithButton)}>
          <span>Freeform options</span>

          {(isMultiselect || freeformOptions.length === 0) && (
            <Button
              size="XS"
              theme="tertiary"
              onClick={() => {
                setFreeformOptions((list) => list.concat("").filter(unique));
              }}
            >
              Add option
              <IconPlusSquare />
            </Button>
          )}
        </div>
      )}
    >
      {freeformOptions.map((freeformOptionValue, index) => (
        <MenuItemFreeformOption
          key={
            /*
             * Use index as key!
             *
             * While editing, the option value will change, but it's
             * still "the same" option item in the list, and should
             * maintain the same component/state (isEditing = true).
             */
            index
          }
          value={freeformOptionValue}
          onChange={(newValue) => {
            const isEnumOption = enumOptions.includes(newValue);
            const isDuplicate = freeformOptions
              .filter((_, otherIndex) => otherIndex !== index)
              .includes(newValue);

            if (isEnumOption || isDuplicate) {
              // Remove
              setFreeformOptions((freeformOptions) =>
                freeformOptions.filter((_, otherIndex) => otherIndex !== index)
              );
            } else {
              // Edit
              setFreeformOptions(
                produce((freeformOptions) => {
                  freeformOptions[index] = newValue;
                })
              );
            }

            if (isDuplicate) {
              return;
            }

            if (isMultiselect) {
              if (isEnumOption) {
                const nextValues = enumOptions.filter((otherOption) =>
                  otherOption === newValue ? true : values.includes(otherOption)
                );

                onChange({
                  ...parameter,
                  values: nextValues,
                });
              } else {
                const isNew = !values.includes(freeformOptionValue);

                onChange({
                  ...parameter,
                  values: isNew
                    ? values.concat(newValue)
                    : values
                        .map((value) =>
                          value === freeformOptionValue ? newValue : value
                        )
                        .filter(unique),
                });
              }
            } else {
              onChange({
                ...parameter,
                value: newValue,
              });
            }
          }}
          onRemove={() => {
            setFreeformOptions((freeformOptions) =>
              freeformOptions.filter((_, otherIndex) => otherIndex !== index)
            );

            if (isMultiselect) {
              onChange({
                ...parameter,
                values: values.filter((value) => value !== freeformOptionValue),
              });
            } else {
              onChange({
                ...parameter,
                value: undefined,
              });
            }
          }}
        />
      ))}
    </Menu.Section>
  );
}
